import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import type { CourseDetail, CourseModule, CourseSummary, CourseTopic } from '../api/types';

/**
 * Course query hooks — backed by `services/course/handler.py`.
 *
 * The API speaks snake_case (name/estimated_hours/module_count/lab_slug); these
 * mappers translate it to the camelCase view types so the UI renders real data
 * instead of blank fields.
 */

const num = (v: unknown, d = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

function mapCourseSummary(raw: any): CourseSummary {
  const s: CourseSummary = {
    id: String(raw?.id ?? ''),
    slug: String(raw?.slug ?? ''),
    title: String(raw?.name ?? raw?.title ?? ''),
    description: String(raw?.description ?? ''),
    difficulty: (raw?.difficulty ?? 'beginner') as CourseSummary['difficulty'],
    estimatedHours: num(raw?.estimated_hours ?? raw?.estimatedHours),
    moduleCount: num(raw?.module_count ?? raw?.modules?.length),
    enrolledCount: num(raw?.enrolled_count),
    ...(raw?.enrolled !== undefined ? { enrolled: Boolean(raw.enrolled) } : {}),
  };
  return raw?.cover_image ? { ...s, coverImageUrl: String(raw.cover_image) } : s;
}

function mapCourseTopic(raw: any, i: number): CourseTopic {
  return {
    slug: String(raw?.slug ?? `topic-${i + 1}`),
    title: String(raw?.title ?? raw?.name ?? ''),
    orderIndex: num(raw?.order_index ?? i),
    body: raw?.body ?? null,
    labId: raw?.lab_slug ?? null,
    scenarioSlug: raw?.scenario_slug ?? null,
    durationMins: num(raw?.estimated_minutes ?? raw?.durationMins ?? 30),
  };
}

function mapCourseModule(raw: any): CourseModule {
  const topics = Array.isArray(raw?.topics)
    ? raw.topics.map(mapCourseTopic).sort((a: CourseTopic, b: CourseTopic) => a.orderIndex - b.orderIndex)
    : [];
  return {
    id: String(raw?.id ?? raw?.slug ?? ''),
    title: String(raw?.name ?? raw?.title ?? ''),
    durationMins: num(raw?.estimated_minutes ?? raw?.durationMins),
    topics,
    gated: false,
    prerequisites: [],
    labId: raw?.lab_slug ?? null,
    scenarioSlug: raw?.scenario_slug ?? null,
  };
}

function mapCourseDetail(raw: any): CourseDetail {
  return {
    ...mapCourseSummary(raw),
    modules: Array.isArray(raw?.modules) ? raw.modules.map(mapCourseModule) : [],
    syllabus: String(raw?.description ?? ''),
    outcomes: raw?.learning_outcomes
      ? String(raw.learning_outcomes).split('\n').map((o: string) => o.trim()).filter(Boolean)
      : [],
  };
}

export const courseKeys = {
  all: ['courses'] as const,
  list: () => [...courseKeys.all, 'list'] as const,
  detail: (id: string) => [...courseKeys.all, 'detail', id] as const,
};

export function useCourses() {
  return useQuery<ReadonlyArray<CourseSummary>>({
    queryKey: courseKeys.list(),
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>('/courses', { method: 'GET', signal });
      const items = Array.isArray(resp) ? resp : ((resp as { items?: unknown[] })?.items ?? []);
      return items.map(mapCourseSummary);
    },
  });
}

/**
 * Assign a course to a cohort = enrol every current cohort member in it, so it
 * shows up in each student's Courses tab. Backed by POST /courses/{slug}/bulk-enroll.
 */
export function useAssignCourseToCohort() {
  const qc = useQueryClient();
  return useMutation<
    { enrolled_count: number; newly_enrolled?: number },
    Error,
    { slug: string; courseId: number; userIds: ReadonlyArray<number> }
  >({
    mutationFn: async ({ slug, courseId, userIds }) =>
      apiFetch('/courses/{slug}/bulk-enroll', {
        method: 'POST',
        pathParams: { slug },
        body: { course_id: courseId, user_ids: [...userIds] },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.all });
    },
  });
}

export function useCourse(id: string | undefined) {
  return useQuery<CourseDetail>({
    queryKey: courseKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>('/courses/{slug}', {
        method: 'GET',
        pathParams: { slug: id ?? '' },
        signal,
      });
      return mapCourseDetail(raw);
    },
  });
}

export interface BulkEnrollInput {
  courseId: string;
  rows: ReadonlyArray<{ email: string; firstName?: string; lastName?: string }>;
}

export interface BulkEnrollResponse {
  enrolled: number;
  skipped: number;
  errors: ReadonlyArray<{ row: number; email: string; reason: string }>;
}

export function useBulkEnroll() {
  const qc = useQueryClient();
  return useMutation<BulkEnrollResponse, Error, BulkEnrollInput>({
    mutationFn: async (input) =>
      apiFetch<BulkEnrollResponse>('/courses/{slug}/bulk-enroll', {
        method: 'POST',
        pathParams: { slug: input.courseId },
        body: input,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: courseKeys.detail(vars.courseId) });
    },
  });
}

export function useEnroll() {
  const qc = useQueryClient();
  return useMutation<void, Error, { courseId: string }>({
    mutationFn: async ({ courseId }) => {
      await apiFetch<void>('/courses/{slug}/enroll', {
        method: 'POST',
        pathParams: { slug: courseId },
        body: { courseId },
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: courseKeys.detail(vars.courseId) });
    },
  });
}
